import React from "react";

/**
 * Floating modal for editing a blog image (hero or mid-article).
 * Shows the image at the top and edit controls below.
 * accentColor: "purple" (hero) | "indigo" (secondary)
 */
const THEME = {
  purple: {
    label:      "text-purple-500",
    activeTab:  "bg-purple-600 text-white",
    inactiveTab:"bg-purple-100 text-purple-700 hover:bg-purple-200",
    input:      "border-purple-300 focus:ring-purple-400",
    applyBtn:   "bg-purple-600 hover:bg-purple-700",
    toggleOn:   "bg-purple-500",
  },
  indigo: {
    label:      "text-indigo-500",
    activeTab:  "bg-indigo-600 text-white",
    inactiveTab:"bg-indigo-100 text-indigo-700 hover:bg-indigo-200",
    input:      "border-indigo-300 focus:ring-indigo-400",
    applyBtn:   "bg-indigo-600 hover:bg-indigo-700",
    toggleOn:   "bg-indigo-500",
  },
};

const BlogImageModal = ({
  imageUrl,
  imageLabel,
  accentColor = "purple",
  imgFormMode,
  setImgFormMode,
  editPrompt,
  setEditPrompt,
  newImgPrompt,
  setNewImgPrompt,
  newImgUseDefault,
  setNewImgUseDefault,
  onEditImage,
  onNewImage,
  onClose,
}) => {
  const t = THEME[accentColor] || THEME.purple;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <p className={`text-xs font-semibold uppercase tracking-wide ${t.label}`}>{imageLabel}</p>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-xl leading-none"
          >
            ✕
          </button>
        </div>

        {/* Image preview */}
        {imageUrl && (
          <div className="px-5 pb-4">
            <img
              src={imageUrl}
              alt={imageLabel}
              className="w-full rounded-xl object-cover"
              style={{ maxHeight: "260px" }}
            />
          </div>
        )}

        {/* Edit controls */}
        <div className="px-5 pb-5">
          {/* Mode tabs */}
          <div className="flex gap-1 mb-4">
            <button
              onClick={() => setImgFormMode("edit")}
              className={`px-3 py-1.5 text-sm rounded-full font-medium transition-colors ${imgFormMode === "edit" ? t.activeTab : t.inactiveTab}`}
            >
              ✏️ Edit current
            </button>
            <button
              onClick={() => setImgFormMode("new")}
              className={`px-3 py-1.5 text-sm rounded-full font-medium transition-colors ${imgFormMode === "new" ? t.activeTab : t.inactiveTab}`}
            >
              🖼 New image
            </button>
          </div>

          {imgFormMode === "edit" ? (
            <div className="flex gap-2">
              <input
                type="text"
                className={`flex-1 border rounded p-2 text-sm text-gray-800 focus:outline-none focus:ring-2 ${t.input}`}
                placeholder="e.g. add more balloons, change background to outdoors…"
                value={editPrompt}
                onChange={e => setEditPrompt(e.target.value)}
                onKeyDown={e => e.key === "Enter" && onEditImage()}
                style={{ color: "#1a1a2e" }}
                autoFocus
              />
              <button
                onClick={onEditImage}
                disabled={!editPrompt.trim()}
                className={`px-4 py-2 text-white text-sm font-medium rounded disabled:opacity-50 whitespace-nowrap ${t.applyBtn}`}
              >
                Apply
              </button>
            </div>
          ) : (
            <div>
              <textarea
                className={`w-full border rounded p-2 text-sm text-gray-800 mb-2 focus:outline-none focus:ring-2 ${t.input}`}
                rows={2}
                placeholder="Custom image prompt (leave blank for AI to generate from post content)"
                value={newImgPrompt}
                onChange={e => setNewImgPrompt(e.target.value)}
                style={{ color: "#1a1a2e" }}
                autoFocus
              />
              <label className="flex items-center gap-2 mb-4 cursor-pointer select-none">
                <div
                  onClick={() => setNewImgUseDefault(v => !v)}
                  className={`relative w-9 h-5 rounded-full transition-colors ${newImgUseDefault ? t.toggleOn : "bg-gray-300"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${newImgUseDefault ? "translate-x-4" : "translate-x-0"}`} />
                </div>
                <span className="text-xs text-gray-600">
                  {newImgUseDefault
                    ? <span>Default style <span className="text-gray-400">(Pixar/Disney, post context appended)</span></span>
                    : <span className="text-gray-400">Default style off — prompt sent as-is</span>}
                </span>
              </label>
              <button
                onClick={onNewImage}
                className={`px-4 py-2 text-white text-sm font-medium rounded ${t.applyBtn}`}
              >
                🖼 Generate New Image
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogImageModal;
